import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import Button from '../../../components/ui/Button';
import UniformMaintenanceModal from '../../../components/modals/UniformMaintenanceModal';
import maintenanceService from '../../../services/maintenanceService';

const Header: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isMaintenanceModalOpen, setIsMaintenanceModalOpen] = useState(false);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMenuOpen(false);
  };

  useEffect(() => {
    // Vérifier le statut de maintenance au chargement
    const checkMaintenance = async () => {
      await maintenanceService.refreshMaintenanceStatus();
    };
    checkMaintenance();
  }, []);

  const handleLogin = () => {
    const maintenanceStatus = maintenanceService.getMaintenanceStatus();
    
    if (!maintenanceService.isLoginAllowed()) {
      // Afficher la popup de maintenance
      setIsMaintenanceModalOpen(true);
    } else {
      // Redirection normale vers la page de connexion
      window.location.href = '/login';
    }
  };

  return (
    <motion.header 
      initial={{ y: -100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6 }}
      className="bg-white shadow-sm border-b border-gray-100 sticky top-0 z-50"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2">
            <motion.div 
              className="flex items-center space-x-2"
              whileHover={{ scale: 1.1 }}
              transition={{ type: "spring", stiffness: 400, damping: 10 }}
            >
              <img 
                src="/logo.png" 
                alt="EspaceResto" 
                className="h-12 w-auto cursor-pointer"
              />
            </motion.div>
          </Link>

          {/* Navigation Desktop */}
          <nav className="hidden md:flex items-center space-x-8">
            <button 
              onClick={() => scrollToSection('pricing')}
              className="text-gray-600 hover:text-orange-500 transition-colors duration-200 font-medium"
            >
              Tarifs
            </button>
            <button 
              onClick={() => scrollToSection('contact')}
              className="text-gray-600 hover:text-orange-500 transition-colors duration-200 font-medium"
            >
              Contactez-nous
            </button>
            <Button 
              onClick={handleLogin}
              variant="outline"
              className="bg-orange-500 hover:bg-orange-600 text-white px-6 py-2 rounded-lg font-semibold transition-colors"
            >
              Connexion
            </Button>
          </nav>

          {/* Menu Mobile */}
          <div className="md:hidden">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="text-gray-600 hover:text-orange-500 transition-colors"
            >
              {isMenuOpen ? (
                <X className="h-6 w-6" />
              ) : (
                <Menu className="h-6 w-6" />
              )}
            </button>
          </div>
        </div>

        {/* Menu Mobile Dropdown */}
        {isMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="md:hidden py-4 border-t border-gray-100"
          >
            <div className="flex flex-col space-y-4">
              <button 
                onClick={() => scrollToSection('pricing')}
                className="text-gray-600 hover:text-orange-500 transition-colors duration-200 font-medium text-left"
              >
                Tarifs
              </button>
              <button 
                onClick={() => scrollToSection('contact')}
                className="text-gray-600 hover:text-orange-500 transition-colors duration-200 font-medium text-left"
              >
                Contactez-nous
              </button>
              <Button 
                onClick={handleLogin}
                variant="outline"
                className="bg-orange-500 hover:bg-orange-600 text-white px-6 py-2 rounded-lg font-semibold transition-colors btn-full"
              >
                Connexion
              </Button>
            </div>
          </motion.div>
        )}
      </div>
      
      {/* Modal de maintenance */}
      <UniformMaintenanceModal
        isOpen={isMaintenanceModalOpen}
        onClose={() => setIsMaintenanceModalOpen(false)}
      />
    </motion.header>
  );
};

export default Header;